import React, { useEffect, useState } from 'react';
import { Calendar, Check, Clock, Plus, Save, Trash2, Ban } from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import TimezoneSelect from '../TimezoneSelect';

const WEEKDAYS = [
  { id: 0, name: 'Monday' },
  { id: 1, name: 'Tuesday' },
  { id: 2, name: 'Wednesday' },
  { id: 3, name: 'Thursday' },
  { id: 4, name: 'Friday' },
  { id: 5, name: 'Saturday' },
  { id: 6, name: 'Sunday' },
];

const AvailabilityManager = ({ onBusinessTimezoneChange }) => {
  const [settings, setSettings] = useState({
    business_timezone: 'Europe/Madrid',
    horizon_days: 28,
    min_notice_hours: 12,
  });
  const [rules, setRules] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingRules, setSavingRules] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  const [newEx, setNewEx] = useState({
    date: '',
    kind: 'extra',
    start_time: '09:00',
    end_time: '13:00',
    slot_interval_minutes: 60,
    note: '',
  });

  const flash = (msg) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(''), 3500);
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getAvailability();
      setSettings(data.settings);
      setRules(data.rules);
      setExceptions(data.exceptions);
      if (onBusinessTimezoneChange) {
        onBusinessTimezoneChange(data.settings.business_timezone);
      }
    } catch (e) {
      setError(e.message || 'Could not load availability settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimezoneOrSettingsChange = async (patch) => {
    setError('');
    try {
      const updated = await adminApi.updateAvailabilitySettings(patch);
      setSettings(updated);
      if (patch.business_timezone && onBusinessTimezoneChange) {
        onBusinessTimezoneChange(updated.business_timezone);
      }
      flash('Availability settings updated!');
    } catch (err) {
      setError(err.message);
    }
  };

  const addWindowForDay = (weekday) => {
    setRules((prev) => [
      ...prev,
      {
        id: `temp-${Date.now()}-${Math.random()}`,
        weekday,
        start_time: '09:00',
        end_time: '13:00',
        slot_interval_minutes: 60,
        is_active: true,
      },
    ]);
  };

  const updateRule = (idx, field, val) => {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  };

  const removeRule = (idx) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveWeeklySchedule = async () => {
    setSavingRules(true);
    setError('');
    try {
      const payload = rules.map((r) => ({
        weekday: Number(r.weekday),
        start_time: r.start_time,
        end_time: r.end_time,
        slot_interval_minutes: Number(r.slot_interval_minutes) || 60,
        is_active: Boolean(r.is_active),
      }));
      const saved = await adminApi.replaceAvailabilityRules(payload);
      setRules(saved);
      flash('Weekly recurring schedule saved!');
    } catch (err) {
      setError(err.message || 'Could not save schedule.');
    } finally {
      setSavingRules(false);
    }
  };

  const handleAddException = async (e) => {
    e.preventDefault();
    if (!newEx.date) return;
    setError('');
    try {
      const payload = {
        date: newEx.date,
        kind: newEx.kind,
        start_time: newEx.kind === 'block' && !newEx.start_time ? null : newEx.start_time || null,
        end_time: newEx.kind === 'block' && !newEx.end_time ? null : newEx.end_time || null,
        slot_interval_minutes: Number(newEx.slot_interval_minutes) || 60,
        note: newEx.note.trim() || null,
      };
      const created = await adminApi.createAvailabilityException(payload);
      setExceptions((prev) => [...prev, created].sort((a, b) => a.date.localeCompare(b.date)));
      setNewEx({
        date: '',
        kind: 'extra',
        start_time: '09:00',
        end_time: '13:00',
        slot_interval_minutes: 60,
        note: '',
      });
      flash(
        payload.kind === 'extra'
          ? `Added extra availability on ${payload.date}`
          : `Blocked ${payload.date}`
      );
    } catch (err) {
      setError(err.message || 'Failed to add date override.');
    }
  };

  const handleDeleteException = async (id) => {
    try {
      await adminApi.deleteAvailabilityException(id);
      setExceptions((prev) => prev.filter((ex) => ex.id !== id));
      flash('Date override removed.');
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-400 py-12 text-center">Loading calendar & availability…</p>;
  }

  return (
    <div className="space-y-8">
      {feedback && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          <Check className="w-4 h-4" />
          {feedback}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Business Timezone & Booking Window Settings */}
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Working Timezone & Booking Window</h2>
            <p className="text-xs text-gray-500">
              All weekly hours and special dates below are interpreted in your working timezone. Visitors
              automatically see slots converted to their own timezone.
            </p>
          </div>
          <TimezoneSelect
            label="Paola's working timezone"
            value={settings.business_timezone}
            onChange={(tz) => handleTimezoneOrSettingsChange({ business_timezone: tz })}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 pt-5 border-t border-gray-100">
          <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">Advance booking horizon</p>
              <p className="text-xs text-gray-500">How many days ahead clients can book</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="7"
                max="120"
                defaultValue={settings.horizon_days}
                onBlur={(e) =>
                  handleTimezoneOrSettingsChange({ horizon_days: Number(e.target.value) || 28 })
                }
                className="w-16 border border-gray-300 rounded-lg px-2 py-1 text-right font-bold text-sm bg-white"
              />
              <span className="text-xs text-gray-500">days</span>
            </div>
          </div>

          <div className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-800">Minimum notice</p>
              <p className="text-xs text-gray-500">Prevent last-minute bookings closer than</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="168"
                defaultValue={settings.min_notice_hours}
                onBlur={(e) =>
                  handleTimezoneOrSettingsChange({ min_notice_hours: Number(e.target.value) || 0 })
                }
                className="w-16 border border-gray-300 rounded-lg px-2 py-1 text-right font-bold text-sm bg-white"
              />
              <span className="text-xs text-gray-500">hours</span>
            </div>
          </div>
        </div>
      </section>

      {/* Date-Specific Overrides (Add Extra Day / Block Holiday) */}
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2.5 rounded-xl bg-purple-50 text-purple-600">
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              Add Specific Bookable Days or Block Holidays
            </h2>
            <p className="text-xs text-gray-500">
              Open a special day/time for bookings or block out vacations & personal appointments.
            </p>
          </div>
        </div>

        <form
          onSubmit={handleAddException}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 bg-gray-50 p-4 rounded-xl border border-gray-200 mb-6"
        >
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Date</label>
            <input
              type="date"
              required
              value={newEx.date}
              onChange={(e) => setNewEx({ ...newEx, date: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Action</label>
            <select
              value={newEx.kind}
              onChange={(e) =>
                setNewEx({
                  ...newEx,
                  kind: e.target.value,
                  start_time: e.target.value === 'block' ? '' : '09:00',
                  end_time: e.target.value === 'block' ? '' : '13:00',
                })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white font-medium"
            >
              <option value="extra">✨ Open extra hours</option>
              <option value="block">🚫 Block day / hours</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Start {newEx.kind === 'block' && '(blank = all day)'}
            </label>
            <input
              type="time"
              required={newEx.kind === 'extra'}
              value={newEx.start_time}
              onChange={(e) => setNewEx({ ...newEx, start_time: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              End {newEx.kind === 'block' && '(blank = all day)'}
            </label>
            <input
              type="time"
              required={newEx.kind === 'extra'}
              value={newEx.end_time}
              onChange={(e) => setNewEx({ ...newEx, end_time: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Note (optional)
            </label>
            <input
              type="text"
              placeholder="e.g. Saturday clinic / Holiday"
              value={newEx.note}
              onChange={(e) => setNewEx({ ...newEx, note: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
            />
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-1.5 bg-primary hover:bg-blue-600 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Date
            </button>
          </div>
        </form>

        {exceptions.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">
            No special dates or blocked holidays configured yet.
          </p>
        ) : (
          <div className="space-y-2">
            {exceptions.map((ex) => (
              <div
                key={ex.id}
                className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3 text-sm"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${
                      ex.kind === 'extra'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {ex.kind === 'extra' ? <Plus className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                    {ex.kind === 'extra' ? 'Extra availability' : 'Blocked'}
                  </span>
                  <span className="font-bold text-gray-900">{ex.date}</span>
                  <span className="text-gray-600">
                    {ex.start_time && ex.end_time
                      ? `${ex.start_time} – ${ex.end_time} (${settings.business_timezone})`
                      : 'Entire day'}
                  </span>
                  {ex.note && <span className="text-xs text-gray-400">· {ex.note}</span>}
                </div>
                <button
                  onClick={() => handleDeleteException(ex.id)}
                  className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                  title="Remove override"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Weekly Recurring Hours */}
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-blue-50 text-primary">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Weekly Recurring Schedule</h2>
              <p className="text-xs text-gray-500">
                Standard hours in <strong>{settings.business_timezone}</strong>.
              </p>
            </div>
          </div>
          <button
            onClick={saveWeeklySchedule}
            disabled={savingRules}
            className="inline-flex items-center gap-2 bg-gray-900 hover:bg-primary text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-colors"
          >
            <Save className="w-4 h-4" />
            {savingRules ? 'Saving…' : 'Save Weekly Schedule'}
          </button>
        </div>

        <div className="space-y-3">
          {WEEKDAYS.map((day) => {
            const dayRules = rules
              .map((r, index) => ({ ...r, _idx: index }))
              .filter((r) => Number(r.weekday) === day.id);

            return (
              <div
                key={day.id}
                className="border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div className="w-32 font-bold text-gray-800 text-sm">{day.name}</div>

                <div className="flex-1 flex flex-wrap items-center gap-2">
                  {dayRules.length === 0 ? (
                    <span className="text-xs text-gray-400 italic">Closed</span>
                  ) : (
                    dayRules.map((r) => (
                      <div
                        key={r.id || r._idx}
                        className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5"
                      >
                        <input
                          type="time"
                          value={r.start_time}
                          onChange={(e) => updateRule(r._idx, 'start_time', e.target.value)}
                          className="bg-transparent text-xs font-semibold text-gray-800 focus:outline-none"
                        />
                        <span className="text-gray-400">–</span>
                        <input
                          type="time"
                          value={r.end_time}
                          onChange={(e) => updateRule(r._idx, 'end_time', e.target.value)}
                          className="bg-transparent text-xs font-semibold text-gray-800 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => removeRule(r._idx)}
                          className="text-gray-400 hover:text-red-600 ml-1"
                          title="Remove window"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => addWindowForDay(day.id)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline self-start sm:self-center"
                >
                  <Plus className="w-3.5 h-3.5" /> Add hours
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default AvailabilityManager;
